import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Modal } from 'semantic-ui-react';
import api from '../../../api';
import { extrairDocumentos } from '../../../utils/documentosPlanilha';

const LINHAS = [
  ['responsaveisCriados', 'Proprietários cadastrados'],
  ['responsaveisAtualizados', 'Proprietários atualizados'],
  ['unidadesCriadas', 'Unidades cadastradas'],
  ['unidadesAtualizadas', 'Unidades atualizadas'],
  ['bloqueadas', 'Unidades bloqueadas por inadimplência'],
  ['desbloqueadas', 'Unidades desbloqueadas'],
];

// Resumo do que o Almah cadastrou/atualizou (usado após importar e após sincronizar)
export const ResumoAlmah = ({ resumo }) => {
  if (!resumo) return null;
  if (resumo.erroGeral) return <div className="va-res-banner falha"><Icon name="times circle outline" /> {resumo.erroGeral}</div>;
  if (resumo.condominioNaoEncontrado) {
    return (
      <div className="va-res-banner falha">
        <Icon name="times circle outline" size="big" />
        <div>
          <b>Condomínio não encontrado no Almah</b>
          <p>Não achei um condomínio no Almah com o nome cadastrado para o CNPJ deste condomínio. Confira o CNPJ e o nome do condomínio.</p>
        </div>
      </div>
    );
  }
  if (resumo.credenciaisRecusadas) {
    return (
      <div className="va-res-banner falha" role="alert">
        <Icon name="times circle outline" size="big" />
        <div>
          <b>O Almah recusou as chaves da integração</b>
          <p>Em Perfil, Integrações, confira se o Client ID e o Token de acesso estão nos campos certos e salve de novo.</p>
        </div>
      </div>
    );
  }
  const itens = LINHAS.filter(([chave]) => resumo[chave] > 0);
  return (
    <div className="va-almah-resumo">
      <div className="va-res-banner sucesso">
        <Icon name="check circle outline" size="big" />
        <div>
          <b>Sincronização com o Almah concluída</b>
          <p>Condomínio no Almah: {resumo.condominioAlmah && resumo.condominioAlmah.nome}</p>
        </div>
      </div>
      {itens.length > 0 ? (
        <ul className="va-almah-itens">
          {itens.map(([chave, rotulo]) => <li key={chave}><b>{resumo[chave]}</b> {rotulo}</li>)}
        </ul>
      ) : <p className="va-almah-nada">Nenhuma alteração: os dados já estavam atualizados.</p>}
      {resumo.naoEncontrados && resumo.naoEncontrados.length > 0 && (
        <p className="va-almah-aviso"><Icon name="warning circle" /> {resumo.naoEncontrados.length} documento(s) não encontrado(s) no Almah.</p>
      )}
      {resumo.semUnidadeNoCondominio && resumo.semUnidadeNoCondominio.length > 0 && (
        <p className="va-almah-aviso"><Icon name="warning circle" /> {resumo.semUnidadeNoCondominio.length} pessoa(s) sem unidade própria neste condomínio.</p>
      )}
      {resumo.documentosInvalidos && resumo.documentosInvalidos.length > 0 && (
        <p className="va-almah-aviso"><Icon name="warning circle" /> Documento(s) inválido(s) ignorado(s): {resumo.documentosInvalidos.join(', ')}.</p>
      )}
      {resumo.limiteExcedido && resumo.limiteExcedido.length > 0 && (
        <p className="va-almah-aviso"><Icon name="warning circle" /> Limite de unidades atingido. Não cadastradas: {resumo.limiteExcedido.join(', ')}. Adicione mais unidades no plano.</p>
      )}
      {resumo.erros > 0 && <p className="va-almah-aviso"><Icon name="warning circle" /> {resumo.erros} consulta(s) com erro. Tente novamente.</p>}
    </div>
  );
};

const ModalImportarAlmah = ({ aberto, onFechar, condominioId, onConcluido }) => {
  const [documentos, setDocumentos] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [resumo, setResumo] = useState(null);
  const [leitura, setLeitura] = useState(null);

  const fechar = () => {
    setResumo(null);
    setErro('');
    setDocumentos('');
    setLeitura(null);
    onFechar();
  };

  const onChangeArquivo = async e => {
    const arquivo = e.target.files && e.target.files[0];
    e.target.value = null;
    if (!arquivo) return;
    setErro('');
    setResumo(null);
    try {
      const { documentos: lista, ignorados } = await extrairDocumentos(arquivo);
      setDocumentos(lista.join('\n'));
      setLeitura({ nome: arquivo.name, total: lista.length, ignorados });
    } catch (err) {
      setLeitura(null);
      setErro(err.message);
    }
  };

  const importar = async () => {
    setLoading(true);
    setErro('');
    setResumo(null);
    try {
      const res = await api.unidade.importarAlmah({ condominio: condominioId, documentos });
      setResumo(res);
      if (onConcluido) onConcluido();
    } catch (e) {
      setErro((e && e.data && e.data.error) || 'Não foi possível consultar o Almah agora.');
    }
    setLoading(false);
  };

  return (
    <Modal open={aberto} onClose={fechar} size="small">
      <Modal.Header>Importar proprietários e unidades do Almah</Modal.Header>
      <Modal.Content>
        <p>
          O Almah consulta pessoas pelo <b>CPF ou CNPJ</b>. Envie a planilha exportada da Almah ou digite os documentos (um por linha, ou separados por vírgula)
          e o sistema busca nome, e-mail, telefone e as unidades de cada um no condomínio, cadastrando ou atualizando tudo.
          Os proprietários que já estão cadastrados são sempre atualizados.
        </p>
        <label htmlFor="planilhaAlmah" className="label-ui-arquivo" style={{ width: '100%' }}>
          <span><Icon name="file excel outline" /> Carregar planilha com CPF/CNPJ (XLS, XLSX ou CSV)</span>
          <input type="file" id="planilhaAlmah" style={{ display: 'none' }} accept=".xls,.xlsx,.csv" onChange={onChangeArquivo} disabled={loading} />
        </label>
        {leitura && (
          <p className="va-almah-leitura">
            <Icon name="check circle" /> <b>{leitura.nome}</b>: {leitura.total} documento{leitura.total !== 1 && 's'} válido{leitura.total !== 1 && 's'} encontrado{leitura.total !== 1 && 's'}
            {leitura.ignorados > 0 && ` (${leitura.ignorados} inválido${leitura.ignorados !== 1 ? 's' : ''} ignorado${leitura.ignorados !== 1 ? 's' : ''})`}.
          </p>
        )}
        <textarea
          className="va-almah-textarea"
          rows={7}
          placeholder={'123.456.789-09\n987.654.321-00\n12.345.678/0001-99'}
          value={documentos}
          onChange={e => setDocumentos(e.target.value)}
          disabled={loading}
        />
        {erro && <div className="va-res-banner falha" role="alert"><Icon name="times circle outline" /> {erro}</div>}
        <ResumoAlmah resumo={resumo} />
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={fechar} className="btn-ui-1 btn-ui-1-basic">{resumo ? 'Fechar' : 'Cancelar'}</Button>
        <Button className="btn-ui-1" loading={loading} disabled={loading} onClick={importar}>
          <Icon name="cloud download" /> Buscar no Almah
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

ModalImportarAlmah.propTypes = {
  aberto: PropTypes.bool.isRequired,
  onFechar: PropTypes.func.isRequired,
  condominioId: PropTypes.string,
  onConcluido: PropTypes.func,
};

export default ModalImportarAlmah;
